/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

import { MarketingOptInChannels, MarketingOptInDismissAction, MarketingOptInSource } from './types';

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when someone views the prompt to subscribe to marketing communications.
 *
 * For more context, check the functional spec:
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/5315821576/FS+M1+Re-engage+Customers+for+Email+WhatsApp+Contactability#Tracking-Requirements
 *
 * &nbsp;
 */
export class OptInModalView extends AnalyticsEvent {
  eventName = 'opt-in-modal-view';

  consentPurpose: ConsentPurpose = 'marketing';

  concept = CONCEPTS.marketing.growth;

  constructor(
    eventProperties: {
      channels: MarketingOptInChannels[];
      marketingOptInSource: MarketingOptInSource;
      marketingOptInVersion: string;
      countryCode?: string;
    },
    observabilityTags: {
      channels: MarketingOptInChannels[];
      marketingOptInSource: MarketingOptInSource;
      marketingOptInVersion: string;
      countryCode?: string;
    }
  ) {
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when someone subscribes to a marketing communication channel.
 *
 * For more context, check the functional spec:
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/5315821576/FS+M1+Re-engage+Customers+for+Email+WhatsApp+Contactability#Tracking-Requirements
 *
 * &nbsp;
 */
export class OptInModalAccept extends AnalyticsEvent {
  eventName = 'opt-in-modal-accept';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.marketing.growth;

  constructor(
    eventProperties: {
      channels: MarketingOptInChannels[];
      marketingOptInSource: MarketingOptInSource;
      marketingOptInVersion: string;
      countryCode?: string;
    },
    observabilityTags: {
      channels: MarketingOptInChannels[];
      marketingOptInSource: MarketingOptInSource;
      marketingOptInVersion: string;
      countryCode?: string;
    }
  ) {
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when someone declines to subscribe to a marketing communication channel.
 *
 * For more context, check the functional spec:
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/5315821576/FS+M1+Re-engage+Customers+for+Email+WhatsApp+Contactability#Tracking-Requirements
 *
 * &nbsp;
 */
export class OptInModalDecline extends AnalyticsEvent {
  eventName = 'opt-in-modal-decline';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.marketing.growth;

  constructor(
    eventProperties: {
      channels: MarketingOptInChannels[];
      marketingOptInSource: MarketingOptInSource;
      marketingOptInVersion: string;
      countryCode?: string;
    },
    observabilityTags: {
      channels: MarketingOptInChannels[];
      marketingOptInSource: MarketingOptInSource;
      marketingOptInVersion: string;
      countryCode?: string;
    }
  ) {
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when someone clicks "Maybe later" or dismisses the modal without taking action.
 *
 * For more context, check the functional spec:
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/5315821576/FS+M1+Re-engage+Customers+for+Email+WhatsApp+Contactability#Tracking-Requirements
 *
 * &nbsp;
 */
export class OptInModalDismiss extends AnalyticsEvent {
  eventName = 'opt-in-modal-dismiss';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.marketing.growth;

  constructor(
    eventProperties: {
      channels: MarketingOptInChannels[];
      marketingOptInSource: MarketingOptInSource;
      marketingOptInVersion: string;
      countryCode?: string;
      action: MarketingOptInDismissAction;
    },
    observabilityTags: {
      channels: MarketingOptInChannels[];
      marketingOptInSource: MarketingOptInSource;
      marketingOptInVersion: string;
      countryCode?: string;
      action: MarketingOptInDismissAction;
    }
  ) {
    super(eventProperties, observabilityTags);
  }
}
